class NewsletterPopup extends ModalDialog {
  constructor() {
    super();
    this.init();
    this.attachEventListeners();
  }

  init() {
    this.delay = Number(this.dataset.delay || 10) * 1000;
    this.closed = getCookie("newsletter-closed");
    this.subscribed = getCookie("newsletter-subscribed");
    this.form = this.querySelector(".js-form");
    this.response = this.querySelector("[data-newsletter-response]");
    this.ageVerificationPopup = document.querySelector("age-verification-popup");
    this.storageKey = `newsletter-attempt-${this.id}`;
  }

  attachEventListeners() {
    const closeButton = this.querySelector(".js-button-close");
    if (closeButton) {
      closeButton.addEventListener("click", () => this.closeNewsletter());
    }

    if (this.form) {
      this.form.addEventListener("submit", () => this.onSubscribe());
    }

    const copyButton = this.querySelector("[data-copy-discount]");
    if (copyButton) {
      copyButton.addEventListener("click", () => this.copyDiscountCode(copyButton));
    }
  }

  closeNewsletter() {
    setCookie("newsletter-closed", "true");
    this.hide();
  }

  connectedCallback() {
    // A normal Shopify customer form posts and reloads the page.
    // Check its SERVER-RENDERED result, rather than treating submit as success.
    if (this.restoreSubmissionResult()) return;

    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") this.show();
      return;
    }

    this.handlePopupVisibility();
  }

  onSubscribe() {
    // This is only an attempt. Do not close the popup or mark the customer
    // subscribed until Shopify confirms success after the form post.
    try {
      sessionStorage.setItem(this.storageKey, "pending");
    } catch (error) {
      // Blocked browser storage must not stop native form submission.
    }
  }

  restoreSubmissionResult() {
    let wasSubmittedHere = false;
    try {
      wasSubmittedHere = sessionStorage.getItem(this.storageKey) === "pending";
    } catch (error) {
      return false;
    }

    if (!wasSubmittedHere || !this.response) return false;

    const status = this.response.dataset.newsletterResponse;
    if (status !== "success" && status !== "error") return false;

    try {
      sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      // Nothing else required.
    }

    if (status === "success") {
      setCookie("newsletter-subscribed", "true");
      this.subscribed = "true";
      const intro = this.querySelector("[data-newsletter-intro]");
      if (intro) intro.hidden = true;
      const closeButton = this.querySelector(".js-button-close");
      if (closeButton) closeButton.textContent = "Close";
    }

    this.show();
    if (status === "success") {
      const successMessage = this.querySelector("[data-newsletter-success]");
      if (successMessage) successMessage.focus({ preventScroll: true });
    }
    return true;
  }

  handlePopupVisibility() {
    if (getCookie("age-verified") !== "true" && this.ageVerificationPopup) {
      this.handleAgeVerificationPopup();
    } else {
      this.showPopupWithDelay();
    }
  }

  handleAgeVerificationPopup() {
    let prevClassState = this.classList.contains("newsletter-popup-is-hidden");
    const observer = new MutationObserver((mutations) => {
      const mutation = mutations[0];
      if (mutation.attributeName !== "class") return;

      const currentClassState = mutation.target.classList.contains("newsletter-popup-is-hidden");
      if (prevClassState !== currentClassState) {
        prevClassState = currentClassState;
        if (!currentClassState) {
          observer.disconnect();
          this.showPopupWithDelay();
        }
      }
    });
    observer.observe(this, { attributes: true, attributeFilter: ["class"] });
  }

  showPopupWithDelay() {
    setTimeout(() => {
      if (getCookie("newsletter-closed") !== "true" && getCookie("newsletter-subscribed") !== "true") {
        this.show();
      }
    }, this.delay);
  }

  async copyDiscountCode(button) {
    const code = this.querySelector("[data-discount-code]")?.textContent.trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      button.textContent = "Copied!";
    } catch (error) {
      button.textContent = "Select code to copy";
      const codeElement = this.querySelector("[data-discount-code]");
      if (codeElement) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(codeElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }
}

if (!customElements.get("newsletter-popup")) {
  customElements.define("newsletter-popup", NewsletterPopup);
}
